import { GetStaticProps } from "next"
import { Box, Container, Grid, Paper } from "@mui/material"

import Link from "components/i18n/Link"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"

import useTranslation from "hooks/useTranslation"

import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { makeStyles } from "makeStyles"
import Contact from "svg/helpCenter/contact.svg"
import Delivery from "svg/helpCenter/delivery.svg"
import ReturnShipment from "svg/helpCenter/returnShipment.svg"
// import ShipmentTracking from "svg/helpCenter/shipmentTracking.svg"
import WarumMitUns from "svg/helpCenter/warumMitUns.svg"
import PaymentMethods from "svg/helpCenter/paymentMethods.svg"
import Assembly from "svg/helpCenter/assembly.svg"
// import BuyingAdvice from "svg/helpCenter/buyingAdvice.svg"
import FAQ from "svg/helpCenter/faq.svg"
import UeberSafehero from "svg/helpCenter/safehero.svg"
import WebsiteInfo from "svg/helpCenter/websiteInfo.svg"
import WebsiteSecurity from "svg/helpCenter/websiteSecurity.svg"
import AppContext from "@secureo/common/context/AppContext/AppContext"
import { useContext } from "react"

export const helpCenterBreadCrumb: BreadCrumbAncestor = {
	label: "Hilfe",
	href: "/help",
}

const useStyles = makeStyles()((theme) => ({
	paper: {
		padding: theme.spacing(2),
		color: theme.palette.secondary.dark,
		display: "flex",
		alignItems: "center",
		minHeight: "80px",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		transition: "0.2s",
		"&:hover": {
			boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			color: "#7abfac",
		},
	},
	icon: {
		width: "40px",
		height: "100%",
		objectFit: "contain",
		marginRight: "20px",
	},
}))

const papers = [
	{
		headlineByLanguage: { de: "Kontakt", pl: "Kontakt" },
		contentByLanguage: {
			de: "Wir freuen uns auf Ihre Anfrage",
			pl: "Czekamy na Państwa zapytanie",
		},
		icon: <Contact />,
		link: "/help/contact",
	},
	{
		headlineByLanguage: { de: "Lieferung", pl: "Dostawa" },
		contentByLanguage: {
			de: "Informationen und Kosten zu unseren Lieferoptionen.",
			pl: "Informacje i koszty dotyczące naszych opcji dostawy.",
		},
		icon: <Delivery />,
		link: "/help/delivery",
	},
	{
		headlineByLanguage: {
			de: "Rücksendung",
			pl: null,
			//"Wróć"
		},
		contentByLanguage: {
			de: "Probleme mit Ihrem Artikel? Widerrufsbelehrung",
			pl: "Masz problem z zakupem? Polityka anulowania",
		},
		icon: <ReturnShipment />,
		link: "/help/return-shipment",
	},
	// {
	// 	headlineByLanguage: {de: "Sendungsverfolgung"},
	// 	contentByLanguage: {de: "Checken Sie den Status Ihrer Paketsendung.",},
	// 	icon: <ShipmentTracking />,
	// 	link: "/help/shipment-tracking",
	// },
	{
		headlineByLanguage: { de: "Warum mit uns?", pl: "Dlaczego z nami?" },
		contentByLanguage: {
			de: "Gute Gründe warum Sie sich für uns entscheiden sollten.",
			pl: "Dobre powody, dla których powinieneś nas wybrać.",
		},
		icon: <WarumMitUns />,
		link: "/help/why-choose-us",
	},
	{
		headlineByLanguage: { de: "Zahlungsmethoden", pl: "Metody płatności" },
		contentByLanguage: {
			de: "Hier finden Sie alles Rund um unsere Zahlungsarten.",
			pl: "Tutaj znajdziesz wszystko o naszych metodach płatności.",
		},
		icon: <PaymentMethods />,
		link: "/help/payments",
	},
	{
		headlineByLanguage: {
			de: "Montage",
			pl: null, //"Montaż"
		},
		contentByLanguage: {
			de: "Gerne übernehmen wir die Montage Ihres Artikels.",
			pl: "Chętnie przejmiemy na siebie montaż Twojego przedmiotu.",
		},
		icon: <Assembly />,
		link: "/help/assembly",
	},
	// {
	// 	headlineByLanguage: {de: "Kaufberatung"},
	// 	contentByLanguage: {de: "Hilfreiche Tipps die Sie vor dem Kauf beachten sollten.",},
	// 	icon: <BuyingAdvice />,
	// },
	{
		headlineByLanguage: { de: "FAQs", pl: "FAQs" },
		contentByLanguage: {
			de: "Häufig gestellte Fragen zu unseren Dienstleistungen.",
			pl: "Najczęściej zadawane pytania dotyczące naszych usług.",
		},
		icon: <FAQ />,
		link: "/help/faq",
	},
	{
		headlineByLanguage: { de: "Über SafeHero", pl: "O SafeHero" },
		contentByLanguage: {
			de: "Erfahren Sie alles über die Personen hinter SafeHero.",
			pl: "Dowiedz się wszystkiego o ludziach stojących za SafeHero.",
		},
		icon: <UeberSafehero />,
		link: "/help/about",
	},
	{
		headlineByLanguage: { de: "Website Info", pl: "Website informacje" },
		contentByLanguage: {
			de: "Datenschutz, Impressum und weitere Rechtliche Infos.",
			pl: "Dowiedz się więcej o SafeHero.",
		},
		icon: <WebsiteInfo />,
		link: "/imprint",
	},
	{
		headlineByLanguage: { de: "Website Security", pl: "Bezpieczeństwo strony internetowej" },
		contentByLanguage: {
			de: "Wir setzen auf die höchsten Sicherheitsstandards.",
			pl: "Stawiamy na najwyższe standardy bezpieczeństwa.",
		},
		icon: <WebsiteSecurity />,
		link: "/help/security",
	},
]

const HelpCenter = () => {
	const { classes } = useStyles()
	const { t } = useTranslation("help")
	const { language } = useContext(AppContext)

	return (
		<>
			<BreadCrumbs
				currentPath={{ ...helpCenterBreadCrumb, label: t(helpCenterBreadCrumb.label) }}
			/>
			<br />
			<Box my={5}>
				<Container>
					<div style={{ textAlign: "center", maxWidth: "800px", margin: "auto" }}>
						<h1>{t("howCanWeHelpYou")}</h1>
						<p>{t("howCanWeHelpYouText")}</p>
					</div>

					<Box my={5} pb={5}>
						<Grid container spacing={2}>
							{papers.map((paper) => {
								const headline = paper.headlineByLanguage[language]

								if (!headline) return null

								const content =
									paper.contentByLanguage[language] ?? paper.contentByLanguage?.de
								return (
									<Grid item xs={12} sm={6} md={4} lg={3} key={headline}>
										<Link href={paper.link}>
											<Paper className={classes.paper}>
												<div className={classes.icon}>{paper.icon}</div>
												<div>
													<Box mb={1}>{headline}</Box>
													<div style={{ color: "grey" }}>{content}</div>
												</div>
											</Paper>
										</Link>
									</Grid>
								)
							})}
						</Grid>
					</Box>
				</Container>
			</Box>
		</>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const props = {
		...(await getServerTranslations(context.locale, [
			"common",
			"countries",
			"languages",
			"help",
		])),
	}

	return {
		props,
	}
}

export default HelpCenter
